/**
 * @fileOverview Base object inherited by transactionDetails and installmentDetails views. This object is responsible for expandable panel behavior.
 * @name SharedPanel
 * @author tsullivan, hkori
 */
define([
	'BaseView',
	'backbone',
	'widgets/sessionTimer',
	'jquery'
], function (BaseView, Backbone, SessionTimer, $) {
	'use strict';

	var SharedPanel = BaseView.extend({

		events: {
			'webkitTransitionEnd': 'transitionEnd',
			'otransitionend': 'transitionEnd',
			'oTransitionEnd': 'transitionEnd',
			'msTransitionEnd': 'transitionEnd',
			'transitionend': 'transitionEnd',

			'click .closeIcon': 'closeInlinePanel'
		},

		// Prefix of event name observed by collection view
		// TODO: override
		eventPrefix: null,

		/*
		 * handle DOM changes when panel needs to be shown or hidden
		 * handle pageName tracking
		 * handle extending the session when no AJAX call is made
		 */
		showHideWithPossiblePreliminaryFetch: function () {
			var modelId = this.model.get('id'),
				eventPrefix = this.eventPrefix,
				sessionTimer;

			/* TODO fire tracking beacon when this is expanded
			 * example: Backbone.trigger('trackPageName', 'thepagename'); */
			if (this.model.get('requestedToShow')) {
				/* Open the Panel */

				if (this.model.get('isRendered')) {
					// was already rendered, just need to show it and reset
					// sessionTimeout counter so user doesn't get prompted to
					// stay logged in
					sessionTimer = new SessionTimer({});
					sessionTimer.extendSession({isTrigger: true});

					this.transitionStart();
				} else {
					// show spinner over transaction row and remove content that was previously rendered
					// if we get here and there was previously rendered content, the content is an error message

					Backbone.trigger(eventPrefix + ':toggleLoading', modelId);
					this.$el.empty();

					this.triggerRender();

					// transitionStart() is called in afterRender */
				}
			} else if (this.model.get('shouldRefresh')) {
				// Refresh the page if shouldRefresh is set to true.
				location.reload(true);
			} else {
				this.transitionStart();

				// fallback for IE9- which doesn't support transitions
				// a delayed style change step that won't affect browsers that work
				setTimeout(function () {
					Backbone.trigger(eventPrefix + ':toggleExpanded', modelId, false);
				}, 750); // timed to fire 50ms after transitionend event
			}
		},

		/*
		 * once txn data is fetched and the template is rendered, remove the
		 * spinner and add a js class to mark the panel container as having hot data
		 */
		afterRender: function () {
			var modelId = this.model.get('id');

			Backbone.trigger(this.eventPrefix + ':toggleLoading', modelId, false); // turn off spinner
			Backbone.trigger(this.eventPrefix + ':toggleExpanded', modelId, true); // add the blue line to the txn row

			if (!this.model.get('isFullPageView')) {
				/* If this is a fullpage view of the panel details (not
				 * inline), then transition events should not be taken */
				 this.transitionStart();
			}

			if (!this.model.get('error')) {
				this.model.set('isRendered', true);
			}

			// reset view defaults in case there was an intermittent error that was rendered
			this.model.unset('error');
		},

		/*
		 * do closing-the-panel stuff by setting a model attribute
		 */
		closeInlinePanel: function (event) {
			event.preventDefault();

			// Refresh the page if shouldRefresh is set to true.
			if (this.model.get('shouldRefresh')) {
				location.reload(true);
			} else {
				this.model.set('requestedToShow', false);
			}
		},

		/*
		 * The 'transitionstart' event doesn't exist, so this function is
		 * manually invoked to separate logic / style
		 *
		 * This can be called whether or not the content has been rendered
		 * before, it handles measuring the height and starting the height
		 * transition from 0px to Xpx.
		 */
		transitionStart: function () {
			var height,
				eventPrefix = this.eventPrefix,
				$thisEl = this.$el,
				thisModel = this.model;

			// prevent tabbing while the txn is in transition
			this.togglePreventTabbing(true);

			/*
			 * Separate work of starting transition to this function so it can
			 * be called in different ways
			 */
			function doTransitionStart(transitionHeight) {
				// https://developer.mozilla.org/en-US/docs/Web/API/window.setTimeout#Minimum_delay_and_timeout_nesting
				var HTML5_MIN_SETTIMEOUT = 4;

				if (thisModel.get('requestedToShow')) {
					/* Panel is starting to show;
					 * first set to 0 height then set to fixed height */
					$thisEl.height('0').removeClass('hide').addClass('panel-transition');

					setTimeout(function () {
						// for Safari, add transition must be OUTSIDE of this block
						$thisEl.height(transitionHeight);
						Backbone.trigger(eventPrefix + ':toggleExpanded', thisModel.get('id'), true); // add the blue line to the txn row
					}, HTML5_MIN_SETTIMEOUT);
				} else {
					/* Panel is starting to hide;
					 * first set to fixed height then set to 0 height */
					$thisEl.height(transitionHeight);

					setTimeout(function () {
						// for Safari, add transition must be INSIDE of this block
						$thisEl.addClass('panel-transition').height('0');
					}, HTML5_MIN_SETTIMEOUT);
				}
			}

			if (thisModel.get('requestedToShow')) {
				// is not showing - have to measure a hidden element
				$thisEl.removeClass('hide');
				height = $thisEl.outerHeight();
				$thisEl.addClass('hide');
			} else {
				// is showing - easy measurement
				height = $thisEl.outerHeight();
			}
			doTransitionStart(height);
		},

		/*
		 * Clean-up function
		 * The 'transitionend' event fires when the 0.5s transition of the height CSS is finished.
		 */
		transitionEnd: function (event) {
			// this is only for when the main container finishes height transition in inline view
			if (this.$el.get(0) !== event.target) {
				return;
			}

			this.$el.removeClass('panel-transition').removeAttr('style');

			if (this.model.get('requestedToShow')) {
				this.$el.removeClass('hide');
				Backbone.trigger(this.eventPrefix + ':toggleExpanded', this.model.get('id'), true); // remove the blue line to the txn row

				/* Panel is shown */
				this.$el.find('.js_closeIcon a').focus();

			} else {
				/* Panel is hidden */
				this.$el.addClass('hide');
				Backbone.trigger(this.eventPrefix + ':toggleExpanded', this.model.get('id'), false); // remove the blue line to the txn row
			}

		},

		/**
		 * Prevents user from tabbing
		 *
 		 * @param preventTabbing {Boolean} - True or false preventing tabbing on the page
		 */
		togglePreventTabbing: function(preventTabbing) {
			if (preventTabbing === false) {
				$('body').off('keydown');
			} else {
				$('body').on('keydown', function(event) {
					if (event.keyCode && event.keyCode === 9) {
						event.preventDefault();
					}
				});
			}
		}

	});

	return SharedPanel;
});



//////////////////
// WEBPACK FOOTER
// ./view/activity/sharedPanel.js
// module id = 763
// module chunks = 0